import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useState, useEffect, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { DialogFooter, DialogHeader } from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useIntruderStore } from '@/store/intruderStore';
import { Checkbox } from '@/components/ui/checkbox';
import { useAlertMetrics } from '@/hooks/useAlertMetrics';
import { useUIPreferencesStore } from '@/store/uiPreferencesStore';
import { Roles, useRouteAccess } from '../Roles/useRouteAccess';
import TenantLogoSpinner from '../Tenants/TenantLogoSpinner';
import { LogoType } from '../Tenants/lookupTenantTheme';
import { Tag } from '@/components/ui/tag';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useCurrentShift, useShiftControls } from '@/lib/shift/shift-controller';
import { CountdownTimer, TimeFromNow, Timer } from '@/components/time/countdown-timer';
import { Bug, GraduationCap, LogOut, Pause, Settings, StopCircle, Wrench } from 'lucide-react';
import { formatDuration, usePeriod } from '@/lib/time';
import { cn } from '@/lib/utils';
import { differenceInSeconds, format, formatDate } from 'date-fns';
import { useDisclosure } from '@/lib/disclosure';
import ErrorBoundary from '../ErrorHandling/ErrorBoundary';
import { useShiftStore } from '@/lib/shift/shift-store';
import { FrontChatButton } from '@/components/FrontChatButton';
import { INACTIVE_PROMPT_ADVANCE_MS } from '@/lib/shift/util';
import {
  hasOnboardingForCurrentRoute,
  restartOnboardingForCurrentRoute,
} from '@/lib/onboardingTours/driver';
import { HELP_CENTER_LINKS } from '@/lib/onboardingTours/helpCenterLinks';
import { FeatureFlagged } from '@/components/internal/feature-flagged';
import { FeatureFlags } from '@/lib/feature-flag';
import debugReportScript from '@/lib/debug/debug-script?raw';
import posthog from 'posthog-js';

type ShiftStatus =
  | 'active'
  | 'onelink'
  | 'inactive'
  | 'almost-inactive'
  | 'live'
  | 'breaking'
  | 'default';

const ShiftStatusConfig = {
  active: {
    badgeClass: 'border-green-600',
    copy: 'On Shift',
    indicatorClass: 'bg-green-600',
  },
  inactive: {
    badgeClass: 'border-gray-300',
    copy: 'No Active Shift',
    indicatorClass: 'bg-gray-300',
  },
  'almost-inactive': {
    badgeClass: 'border-yellow-600 animate-pulse',
    copy: 'Idle',
    indicatorClass: 'bg-yellow-600',
  },
  breaking: {
    badgeClass: 'border-purple-600',
    copy: 'On Break',
    indicatorClass: 'bg-purple-600',
  },
  default: {
    badgeClass: 'border-gray-300',
    copy: 'No Active Shift',
    indicatorClass: 'bg-gray-300',
  },
  live: {
    badgeClass: 'border-red-800',
    copy: 'Do not disturb',
    indicatorClass: 'bg-red-800',
  },
  onelink: {
    badgeClass: 'border-green-600',
    copy: 'OneLink Shift',
    indicatorClass: 'bg-green-600',
  },
} satisfies Record<
  ShiftStatus,
  {
    badgeClass: string;
    copy: string;
    indicatorClass: string;
  }
>;

interface ShiftManagerProps {
  className?: string;
}

interface GamepadState {
  connected: boolean;
  name: string | null;
  batteryLevel: number | null;
  id: string;
}

interface HapticActuatorWithBattery {
  battery?: { level: number };
}

interface GamepadWithHaptics extends Omit<Gamepad, 'hapticActuators'> {
  hapticActuators?: {
    [key: number]: HapticActuatorWithBattery;
  };
}

const ShiftManager = ({ className }: ShiftManagerProps) => {
  const endShiftDialog = useDisclosure();
  const breakDialog = useDisclosure();
  const settingsDialog = useDisclosure();
  const { user, logout, tenant } = useAuthStore();
  const hasTourForCurrentPage = hasOnboardingForCurrentRoute();
  const shift = useCurrentShift();
  const controls = useShiftControls();
  const breakStartedAt = useShiftStore((s) => s.breakStartedAt);
  const isOnBreak = breakStartedAt != null;
  const hasShift = shift.shift != null;
  const now = usePeriod(() => new Date(), 10 * 1000);
  const secondsUntilIdleness = differenceInSeconds(shift.idleAt.getTime(), now.getTime());
  const isAlmostInactive =
    shift.status !== 'idle' &&
    secondsUntilIdleness > 0 &&
    secondsUntilIdleness < INACTIVE_PROMPT_ADVANCE_MS / 1000 + 30; // Within 30s of wellness check
  const { hasRoleAccess } = useRouteAccess();
  const shiftStart = shift.shift?.start ? new Date(shift.shift.start) : undefined;
  const isLive = useIntruderStore((state) => state.isLive);
  const navigate = useNavigate();
  const [gamepadState, setGamepadState] = useState<GamepadState>({
    connected: false,
    name: null,
    batteryLevel: null,
    id: '',
  });
  const { trackShiftBreak, trackShiftResume, trackShiftStart, trackShiftEnd } = useAlertMetrics();
  const {
    useNewAlertSorting,
    setUseNewAlertSorting,
    viewNextAlertAutomatically,
    setViewNextAlertAutomatically,
  } = useUIPreferencesStore();
  const { toast } = useToast();

  const startErrorMsg = (() => {
    if (!shift.startError) return;
    if (shift.startError.response?.status === 400) return 'You already have an active shift';
    return 'Failed to start shift. Please try again.';
  })();

  useEffect(() => {
    const handleGamepadConnected = (e: GamepadEvent) => {
      const gamepad = e.gamepad as GamepadWithHaptics;
      if ('hapticActuators' in gamepad && gamepad.hapticActuators?.[0]) {
        const actuator = gamepad.hapticActuators[0];
        setGamepadState({
          connected: true,
          name: gamepad.id,
          batteryLevel: actuator.battery?.level ?? null,
          id: gamepad.id,
        });
      } else {
        setGamepadState({
          connected: true,
          name: gamepad.id,
          batteryLevel: null,
          id: gamepad.id,
        });
      }
    };

    const handleGamepadDisconnected = () => {
      setGamepadState({
        connected: false,
        name: null,
        batteryLevel: null,
        id: '',
      });
    };

    // Check for already connected gamepads
    const gamepads = navigator.getGamepads();
    for (const gamepad of gamepads) {
      if (gamepad) {
        setGamepadState({
          connected: true,
          name: gamepad.id,
          batteryLevel: null,
          id: gamepad.id,
        });
        break;
      }
    }

    window.addEventListener('gamepadconnected', handleGamepadConnected);
    window.addEventListener('gamepaddisconnected', handleGamepadDisconnected);

    return () => {
      window.removeEventListener('gamepadconnected', handleGamepadConnected);
      window.removeEventListener('gamepaddisconnected', handleGamepadDisconnected);
    };
  }, []);

  const handleStartShift = async () => {
    try {
      console.debug('Starting shift from ShiftManager');
      await controls.startShift();
      trackShiftStart();
      navigate('/monitor');
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to start shift:', error);
    }
  };

  const handleEndShift = async () => {
    await controls.endShift({ clean: true });
    navigate('/dashboard');
    trackShiftEnd();
  };

  const endBreak = () => {
    controls.endBreak();
    breakDialog.onClose();
    trackShiftResume();
  };

  const startBreak = async () => {
    controls.startBreak();
    breakDialog.onOpen();
    trackShiftBreak();
  };

  const handleRestartTour = async () => {
    if (!user?.id) {
      return;
    }
    await restartOnboardingForCurrentRoute(user.id);
  };

  const hasMonitoringAccess = hasRoleAccess(Roles.Monitoring);

  const status = useMemo((): ShiftStatus => {
    if (!hasShift && !shift.requiresShift && hasMonitoringAccess) return 'onelink';
    if (isOnBreak) return 'breaking';
    if (!hasShift || shift.status === 'idle') return 'inactive';
    if (isAlmostInactive) return 'almost-inactive';
    if (isLive) return 'live';
    return 'active';
  }, [
    hasShift,
    shift.status,
    shift.requiresShift,
    isAlmostInactive,
    isLive,
    isOnBreak,
    hasMonitoringAccess,
  ]);

  const statusConfig = ShiftStatusConfig[status];

  const checkForControllers = () => {
    const gamepads = navigator.getGamepads();
    let controllerFound = false;

    for (const gamepad of gamepads) {
      if (gamepad) {
        controllerFound = true;
        const gamepadWithHaptics = gamepad as GamepadWithHaptics;
        if ('hapticActuators' in gamepadWithHaptics && gamepadWithHaptics.hapticActuators?.[0]) {
          const actuator = gamepadWithHaptics.hapticActuators[0];
          setGamepadState({
            connected: true,
            name: gamepad.id,
            batteryLevel: actuator.battery?.level ?? null,
            id: gamepad.id,
          });
        } else {
          setGamepadState({
            connected: true,
            name: gamepad.id,
            batteryLevel: null,
            id: gamepad.id,
          });
        }
        return;
      }
    }

    // No controllers found
    if (!controllerFound) {
      toast({
        title: 'No Controllers Found',
        description:
          'No game controllers were detected. Connect a controller and press a button on it.',
        variant: 'default',
      });
    }

    setGamepadState({
      connected: false,
      name: null,
      batteryLevel: null,
      id: '',
    });
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="shift-manager"
            className={`flex min-h-[3.5rem] min-w-[320px] items-center justify-between gap-4 border-l pr-3 text-start text-white transition-colors hover:bg-lime/5 ${className}`}
          >
            <div className="flex items-center gap-3">
              <div className="relative"></div>
              <img src="/icon/chevron-down.svg" alt="menu" className="h-4 w-4" />
              <div className="text-sm">
                <p className="text-lime">
                  <strong>{`${user?.firstName} ${user?.lastName}`}</strong>
                </p>
                <p className="text-gray-300">{tenant && <span>{tenant?.tenantName || ''}</span>}</p>
                {/* <p className="text-gray-300">{companyName || 'No org '}</p> */}
              </div>
            </div>

            {hasRoleAccess(Roles.Monitoring) && (
              <div className="relative">
                <div
                  data-testid="shift-status-badge"
                  className={`rounded-full border-2 px-4 py-1.5 ${statusConfig.badgeClass} text-xs font-semibold text-white hover:opacity-90`}
                  style={status === 'almost-inactive' ? { animationDuration: '2000ms' } : {}}
                >
                  <p>{statusConfig.copy}</p>
                </div>
              </div>
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="min-w-[320px] p-0 font-semibold text-lime" align="start">
          {hasRoleAccess(Roles.Monitoring) && shift.requiresShift && (
            <div className="flex flex-col gap-4 px-2 py-4">
              <div>
                <div
                  className={cn('mb-2 flex flex-col rounded-md border bg-muted/10 p-3', {
                    'bg-green-darker': status === 'active',
                  })}
                >
                  <div className="flex justify-between">
                    <div
                      data-testid="shift-status-dropdown"
                      className="flex items-center gap-x-2 text-white"
                    >
                      <span
                        className={cn`h-3 w-3 rounded-full bg-red-400 ${statusConfig.indicatorClass}`}
                      />
                      <p>{statusConfig.copy}</p>
                    </div>
                    <p className="font-bold opacity-80">
                      {shiftStart && (
                        <Timer time={shiftStart} format={(ms) => formatDuration(ms, 'short')} />
                      )}
                    </p>
                  </div>

                  {shift.shift != null && (
                    <>
                      <div className="w-full text-xs text-white opacity-60">
                        <div className="border-muted/150 border-t py-1 font-medium">
                          <p>{shiftStart && `Shift started ${formatDate(shiftStart, 'p')}`}</p>
                        </div>
                        {shift.lastActiveAt != null &&
                          differenceInSeconds(now, shift.lastActiveAt) >= 30 && (
                            <p
                              className={cn('font-normal', {
                                'text-yellow-500': status === 'almost-inactive',
                              })}
                            >
                              Active <TimeFromNow time={shift.lastActiveAt} /> (
                              {format(shift.lastActiveAt, 'p')})
                            </p>
                          )}
                        {status === 'almost-inactive' && (
                          <p>
                            Idleness check in... <CountdownTimer time={shift.inactiveAt} />
                          </p>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {!shift.shift ? (
                  <>
                    {isOnBreak ? (
                      <>
                        <DropdownMenuItem
                          onClick={endBreak}
                          className="justify-center rounded-md border-lime bg-lime/20 px-3 py-2 text-lime hover:!bg-lime/30"
                        >
                          Resume Shift
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem
                          onClick={handleStartShift}
                          className="justify-center rounded-md border-lime bg-lime/20 px-3 py-2 text-lime"
                        >
                          Start Shift
                        </DropdownMenuItem>
                      </>
                    )}
                  </>
                ) : (
                  <div className="mx-1 grid grid-cols-2 gap-x-2">
                    <DropdownMenuItem
                      onClick={startBreak}
                      className="justify-center rounded-md border-purple-400 bg-purple-500/20 px-3 py-2 text-purple-300 hover:!bg-purple-500/30"
                    >
                      <Pause />
                      Take Break
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleEndShift}
                      className="justify-center rounded-md border-red-700 bg-red-500/20 px-3 py-2 text-red-300 hover:!bg-red-500/30"
                    >
                      <StopCircle />
                      End Shift
                    </DropdownMenuItem>
                  </div>
                )}
              </div>

              {startErrorMsg && (
                <div className="mb-4 px-6">
                  <div className="relative rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
                    {startErrorMsg}
                  </div>
                </div>
              )}
            </div>
          )}
          <h3 className="border-t p-3 pb-1 text-sm font-semibold text-white">Account options</h3>
                  <DropdownMenuItem
            onClick={() => settingsDialog.onOpen()}
            className="rounded-t-none px-3 py-3"
          >
            <Settings className="h-4 w-4" />
            Settings
          </DropdownMenuItem>

          {hasTourForCurrentPage && (
            <DropdownMenuItem onClick={handleRestartTour} className="rounded-t-none px-3 py-3">
              <GraduationCap className="h-4 w-4" />
              Restart Tutorial
            </DropdownMenuItem>
          )}
  

          <DropdownMenuItem className="rounded-t-none px-0 py-0">
            <FrontChatButton />
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="rounded-t-none px-3 py-3">
            <a href={HELP_CENTER_LINKS.contactUs} target="_blank" rel="noopener noreferrer">
              <Bug className="h-4 w-4" />
              Report an Issue
            </a>
          </DropdownMenuItem>
          <FeatureFlagged name={FeatureFlags.WatchfulOnly}>
            <DropdownMenuItem
              className="rounded-t-none px-3 py-3"
              onClick={() => {
                posthog.capture('debug_log_captured', {
                  source: 'shift_manager',
                  url: location.href,
                  console_log_count: (window.__debugLogs || []).length,
                });
                try {
                  new Function(debugReportScript)();
                } catch (err) {
                  console.error('Failed to execute debug script:', err);
                }
              }}
            >
              <Wrench className="h-4 w-4" />
              Debug
            </DropdownMenuItem>
          </FeatureFlagged>
          <DropdownMenuItem onClick={() => logout()} className="rounded-t-none px-3 py-3 border-t">
            <LogOut className="h-4 w-4" />
            Log Out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog {...endShiftDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="border-none">
            <DialogTitle className="text-center text-2xl">Confirm end of shift?</DialogTitle>
          </DialogHeader>
          <DialogFooter className="pt-0">
            <div className="flex justify-center gap-4">
              <Button onClick={handleEndShift} disabled={shift.isEnding} className="w-32">
                {shift.isEnding ? 'Ending Shift...' : 'End Shift'}
              </Button>
              <Button variant="outline" onClick={endShiftDialog.onClose} className="w-32">
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog {...settingsDialog}>
        <DialogContent className="sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle>Settings</DialogTitle>
          </DialogHeader>
          <Tabs defaultValue="alerts" className="w-full">
            <TabsList className="mb-0 w-full rounded-none border-x-0 border-t-0">
              <TabsTrigger value="alerts">Alerts</TabsTrigger>
              <TabsTrigger value="account">Account</TabsTrigger>
              <TabsTrigger value="controls">Controls</TabsTrigger>
            </TabsList>

            <TabsContent value="alerts" className="space-y-2 p-4">
              <h3 className="border-[#2E3835] font-semibold">Alert Settings</h3>

              <div className="cursor-pointer rounded-lg border border-[#2E3835] bg-card2 hover:bg-[#1a201f]">
                <label className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="view-next-alert"
                      checked={viewNextAlertAutomatically}
                      onCheckedChange={setViewNextAlertAutomatically}
                    />
                    <div>
                      <label htmlFor="view-next-alert" className="cursor-pointer font-semibold">
                        View Next Alert Automatically
                      </label>
                      <p className="text-gray-400">
                        Enables quick navigation to the next alert in the list.
                      </p>
                    </div>
                  </div>
                </label>
              </div>

              <div className="cursor-pointer rounded-lg border border-[#2E3835] bg-card2 hover:bg-[#1a201f]">
                <label className="flex items-center justify-between gap-4 p-4">
                  <div className="flex items-center gap-4">
                    <Checkbox
                      id="use-new-alert-sorting"
                      checked={useNewAlertSorting}
                      onCheckedChange={setUseNewAlertSorting}
                    />
                    <div>
                      <label
                        htmlFor="use-new-alert-sorting"
                        className="cursor-pointer font-semibold"
                      >
                        Use New Alert Sorting
                      </label>
                      <p className="text-gray-400">
                        Enables improved alert organization with separate sections for urgent and
                        standard alerts
                      </p>
                    </div>
                  </div>
                </label>
              </div>
            </TabsContent>
            <TabsContent value="account" className="p-4">
              <div className="cursor-pointer rounded-lg border border-[#2E3835] bg-card2 p-6 hover:bg-[#1a201f]">
                <div className="flex flex-col gap-6">
                  {/* Header with tenant info and logo */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-white">{tenant?.tenantName}</h3>
                    {tenant && (
                      <TenantLogoSpinner
                        tenantDetails={tenant}
                        logoType={LogoType.DarkPrimary}
                        className="h-10"
                      />
                    )}
                  </div>

                  {/* User information section */}
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <p className="text-sm text-gray-400">User</p>
                      <p className="text-lg">
                        {user?.firstName} {user?.lastName}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <p className="text-sm text-gray-400">Email</p>
                      <p className="text-lg">{user?.email}</p>
                    </div>

                    <div className="space-y-2">
                      <p className="text-sm text-gray-400">Roles</p>
                      <div className="flex flex-wrap gap-2">
                        {tenant?.roles?.map((role, i) => (
                          <Tag key={i} color="green" className="text-white">
                            {role}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="controls" className="p-4">
              <div className="rounded-lg border p-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div
                      className={`h-3 w-3 rounded-full ${gamepadState.connected ? 'bg-green-500' : 'bg-red-500'}`}
                    />
                    <div>
                      <h3 className="font-semibold">Controller Status</h3>
                      <span>
                        {gamepadState.connected
                          ? `Connected: ${gamepadState.name}${gamepadState.batteryLevel !== null ? ` (Battery: ${Math.round(gamepadState.batteryLevel * 100)}%)` : ''}`
                          : 'No controller detected'}
                      </span>
                    </div>
                  </div>
                  <Button variant="outline" onClick={checkForControllers}>
                    Check for controllers
                  </Button>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default function ShiftManagerWrapper(props: ShiftManagerProps) {
  return (
    <ErrorBoundary fallback={<div>An error occurred</div>}>
      <ShiftManager {...props} />
    </ErrorBoundary>
  );
}
